/**
 * Bottom sheet: drag handle, top radius, scrim tap-to-close, and a drag-to-dismiss gesture
 * that mirrors the design's spring. Dragging is applied straight to the element's transform
 * (never through React state) so the gesture stays on the compositor at 60fps.
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { haptic } from '../hooks/useTg';
import { useI18n } from '../lib/i18n';

const DISMISS_DISTANCE = 110;
const DISMISS_VELOCITY = 620;

export function BottomSheet({
  open,
  onClose,
  title,
  action,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Trailing control in the header, e.g. "Reset". */
  action?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useI18n();
  const sheet = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    y0: number;
    cur: number;
    last: number;
    t: number;
    v: number;
    active: boolean;
  } | null>(null);

  // Escape closes; body scroll locks so the page behind cannot move under the sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Scrollable regions inside the sheet own their own gesture.
    if ((e.target as HTMLElement).closest('[data-noswipe]')) return;
    const el = sheet.current;
    if (!el) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture unsupported */
    }
    el.style.animation = 'none';
    el.style.transition = 'none';
    drag.current = {
      y0: e.clientY,
      cur: 0,
      last: e.clientY,
      t: performance.now(),
      v: 0,
      active: true,
    };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current;
    if (!d?.active) return;
    // Upward drag is rubber-banded — the sheet is already at its top stop.
    let dy = e.clientY - d.y0;
    if (dy < 0) dy *= 0.22;
    const now = performance.now();
    const dt = now - d.t;
    if (dt > 4) {
      d.v = ((e.clientY - d.last) / dt) * 1000;
      d.t = now;
      d.last = e.clientY;
    }
    d.cur = dy;
    if (sheet.current) sheet.current.style.transform = `translate3d(0, ${dy}px, 0)`;
  }, []);

  const onPointerUp = useCallback(() => {
    const d = drag.current;
    const el = sheet.current;
    if (!d?.active || !el) return;
    d.active = false;
    if (d.cur > DISMISS_DISTANCE || d.v > DISMISS_VELOCITY) {
      haptic('light');
      el.style.transition = 'transform 240ms var(--pv-ease-sheet)';
      el.style.transform = 'translate3d(0, 110%, 0)';
      window.setTimeout(onClose, 200);
    } else {
      el.style.transition = 'transform 380ms var(--pv-ease)';
      el.style.transform = 'translate3d(0, 0, 0)';
    }
  }, [onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label={t.close}
        onClick={onClose}
        className="pv-fade-in absolute inset-0 bg-scrim"
      />
      <div
        ref={sheet}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="pv-sheet-in absolute inset-x-0 bottom-0 flex max-h-[82%] flex-col rounded-t-sheet bg-surface"
        style={{ touchAction: 'none' }}
      >
        <div className="flex flex-none cursor-grab justify-center pb-1 pt-2">
          <span aria-hidden="true" className="h-1 w-9 rounded-full bg-chevron" />
        </div>

        {(title || action) && (
          <div className="flex flex-none items-center justify-between gap-3 px-gutter pb-3 pt-1.5">
            <h2 className="text-title font-semibold text-text">{title}</h2>
            {action}
          </div>
        )}

        <div data-noswipe="1" className="sc min-h-0 flex-1 overflow-y-auto px-gutter pb-2">
          {children}
        </div>

        {footer ? (
          <div
            className="flex-none px-gutter pt-2"
            style={{ paddingBottom: 'calc(var(--pv-space-5) + var(--pv-safe-bottom))' }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A radio-style option list inside a sheet (filters, pickers). */
export function SheetOptionGroup({
  label,
  options,
}: {
  label: string;
  options: { value: string; label: string; selected: boolean; onSelect: () => void }[];
}) {
  return (
    <div className="mb-4">
      <div className="px-1 pb-2 text-footnote font-semibold uppercase tracking-wide text-hint">
        {label}
      </div>
      <div className="overflow-hidden rounded-card bg-fill">
        {options.map((o, i) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={o.selected}
            onClick={() => {
              haptic('light');
              o.onSelect();
            }}
            className={`flex min-h-tap w-full items-center gap-2.5 px-3.5 py-3 text-start transition-colors duration-fast ease-pv active:bg-pressed-soft ${
              i === options.length - 1 ? '' : 'hairline-b'
            }`}
          >
            <span className="flex-1 text-body text-text">{o.label}</span>
            {o.selected && <CheckMark />}
          </button>
        ))}
      </div>
    </div>
  );
}

function CheckMark() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-none text-accent"
      aria-hidden="true"
    >
      <path d="m5 13 4.5 4.5L19.5 7" />
    </svg>
  );
}
